import { useRef, useState } from 'react';
import { Button } from '@dreamtree-org/twreact-ui';
import { ImagePlus, Trash2 } from 'lucide-react';
import ProductImage from '@/components/ProductImage';
import { ACCEPTED_TYPES, fileToDataUrl } from '@/lib/image';

/**
 * The photo field.
 *
 * The chosen file is downscaled and previewed IMMEDIATELY, before any upload:
 * the preview is the very data URL that will be sent, so what a user approves
 * is exactly what gets stored. The upload itself is deferred to the parent form
 * — a photo picked while creating a product has no product id to attach to yet,
 * so the form saves the row first and posts the photo second.
 */
export default function ImageField({ value, name, onChange, onError, disabled = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset the input so re-choosing the SAME file still fires a change event.
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      onChange(await fileToDataUrl(file));
    } catch (ex) {
      onError && onError(ex.message || 'That image could not be read');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
      <ProductImage src={value} name={name} className="h-28 w-28 shrink-0 border border-[var(--app-border)]" />
      <div className="space-y-2">
        <input ref={inputRef} type="file" accept={ACCEPTED_TYPES} onChange={pick} className="hidden" />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={busy}
            disabled={disabled}
            leftIcon={<ImagePlus size={15} />}
            onClick={() => inputRef.current && inputRef.current.click()}
          >
            {value ? 'Replace photo' : 'Add photo'}
          </Button>
          {value ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              leftIcon={<Trash2 size={15} />}
              onClick={() => onChange(null)}
            >
              Remove
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-[var(--app-muted)]">
          JPEG, PNG or WebP. Large photos are shrunk in your browser before upload.
        </p>
      </div>
    </div>
  );
}
